import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Bloom, EffectComposer, Vignette } from "@react-three/postprocessing";
import { useReducedMotion } from "framer-motion";

/**
 * WorldCanvas — die minimalistische 3D-Welt hinter allen Sections nach dem Hero.
 *
 * Pro Kapitel EIN ruhiges Linien-Objekt (dünne Gold-/Silber-Kanten, kein
 * Partikelstaub): Interface-Panels (Webdesign), gefalteter Stoff (Clothing),
 * Monolith (Brand), Ring (Portfolio), Portal (Kontakt).
 *
 * Kapitelwechsel = Origami: Das aktive Objekt FALTET sich zusammen (kippt nach
 * hinten, sinkt zu einer Linie), das nächste ZIEHT sich von unten hoch und
 * entfaltet sich. Alles strikt an den Scroll-Fortschritt W (0..1) gebunden —
 * kein Zufall, kein Nachlaufen, nichts bleibt zurück.
 *
 * Performance: eine Handvoll Draw-Calls, frameloop="demand" (rendert nur bei
 * Scroll-Änderung). Bloom + Vignette nur auf Desktop; Mobile rendert roh.
 * Reduced-Motion: gar nicht gerendert.
 */

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const seg = (p, s, e) => (e - s < 1e-6 ? 0 : clamp01((p - s) / (e - s)));
const smooth = (t) => t * t * (3 - 2 * t);
const lerp = (a, b, t) => a + (b - a) * t;

const GOLD = "#d4af37";
const SILVER = "#b9bcc4";

// Fallback-Keyframes [progress, formationIndex], falls DOM-Messung scheitert
const DEFAULT_KF = [
  [0.0, 0],
  [0.06, 0],
  [0.14, 1],
  [0.3, 1],
  [0.38, 2],
  [0.5, 2],
  [0.58, 3],
  [0.68, 3],
  [0.75, 4],
  [0.84, 4],
  [0.9, 5],
  [1.0, 5],
];

// Anker-Sections → Formationen
const ANCHOR_MAP = [
  { id: "webdesign", formation: 1 },
  { id: "clothing-design", formation: 2 },
  { id: "clothing-brand", formation: 3 },
  { id: "portfolio", formation: 4 },
  { id: "kontakt", formation: 5 },
];

// Echte DOM-Positionen → Keyframes mit Haltephasen.
// Fortschritt p einer Dokumentposition d (offset ["start end","end end"]):
// p = (d + vh) / H — Formation komplett, sobald die Section hereinläuft,
// gehalten, bis sie weicht; dazwischen faltet die Welt um.
function measureKeyframes(worldEl) {
  try {
    const H = worldEl.offsetHeight;
    const vh = window.innerHeight;
    if (!H || H < 100) return null;

    const relTop = (el) => {
      let top = 0;
      let node = el;
      while (node && node !== worldEl) {
        top += node.offsetTop;
        node = node.offsetParent;
      }
      return top;
    };

    const kf = [
      [0, 0],
      [0.04, 0], // Leere Bühne direkt nach dem Hero
    ];
    let lastP = 0.04;

    for (const { id, formation } of ANCHOR_MAP) {
      const el = worldEl.querySelector(`#${id}`);
      if (!el) continue; // per KI-Manager ausgeblendet → überspringen
      const top = relTop(el);
      const h = el.offsetHeight || vh;
      let pA = clamp01((top + vh * 0.35) / H);
      let pB = clamp01((top + h + vh * 0.55) / H);
      pA = Math.min(Math.max(pA, lastP + 0.02), 0.993);
      pB = Math.min(Math.max(pB, pA + 0.01), 0.995);
      // Lange Lücken (z. B. Über mich / Partner ohne eigene Formation):
      // vorherige Formation HALTEN und erst kurz vor dem nächsten Anker falten.
      const MORPH_WINDOW = 0.1;
      if (pA - lastP > MORPH_WINDOW) {
        kf[kf.length - 1][0] = pA - MORPH_WINDOW;
        lastP = pA - MORPH_WINDOW;
      }
      kf.push([pA, formation], [pB, formation]);
      lastP = pB;
    }
    if (kf.length < 4) return null;
    kf.push([1.0, kf[kf.length - 1][1]]);
    return kf;
  } catch (_) {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Geometrie-Bausteine (nur Kanten/Linien)
// ---------------------------------------------------------------------------
function rectEdges(w, h) {
  return new THREE.EdgesGeometry(new THREE.PlaneGeometry(w, h));
}
function boxEdges(w, h, d) {
  return new THREE.EdgesGeometry(new THREE.BoxGeometry(w, h, d));
}
function circleLine(r, n = 160) {
  const pts = [];
  for (let i = 0; i <= n; i++) {
    const a = (i / n) * Math.PI * 2;
    pts.push(new THREE.Vector3(Math.cos(a) * r, Math.sin(a) * r, 0));
  }
  return new THREE.BufferGeometry().setFromPoints(pts);
}
function segmentLine(ax, ay, az, bx, by, bz) {
  return new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(ax, ay, az), new THREE.Vector3(bx, by, bz)]);
}
// Stoffbahn: Plane-Gitter, das in sanften Falten liegt
function clothGrid(w, h, sx, sy) {
  const g = new THREE.PlaneGeometry(w, h, sx, sy);
  const pos = g.getAttribute("position");
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    pos.setZ(i, Math.sin(x * 1.15) * 0.55 + Math.cos(y * 0.9 + x * 0.4) * 0.25);
  }
  return new THREE.WireframeGeometry(g);
}

// Eine Linie/Kante mit eigener Farbe innerhalb einer Formation
function Edge({ geo, color, opacity = 1, position, rotation }) {
  return (
    <lineSegments geometry={geo} position={position} rotation={rotation}>
      <lineBasicMaterial color={color} transparent opacity={opacity} />
    </lineSegments>
  );
}
function Loop({ geo, color, opacity = 1, position, rotation }) {
  return (
    <line geometry={geo} position={position} rotation={rotation}>
      <lineBasicMaterial color={color} transparent opacity={opacity} />
    </line>
  );
}

// ---------------------------------------------------------------------------
// Formationen 1–5 (Formation 0 = leere Bühne)
// ---------------------------------------------------------------------------
function FormationWeb({ g }) {
  return (
    <>
      <Edge geo={g.panelA} color={SILVER} opacity={0.4} position={[-2.3, 0.5, -1.4]} />
      <Edge geo={g.lineA} color={GOLD} opacity={0.8} position={[-2.3, 0.5, -1.4]} />
      <Edge geo={g.panelB} color={SILVER} opacity={0.45} position={[1.9, -0.4, 0.3]} />
      <Edge geo={g.lineB} color={GOLD} opacity={0.9} position={[1.9, -0.4, 0.3]} />
      <Edge geo={g.panelC} color={SILVER} opacity={0.3} position={[-0.2, 1.6, 1.7]} />
    </>
  );
}
function FormationCloth({ g }) {
  return <Edge geo={g.cloth} color={SILVER} opacity={0.16} rotation={[-0.95, 0.12, 0]} position={[0, -1.6, -1.5]} />;
}
function FormationMonolith({ g }) {
  return (
    <>
      <Edge geo={g.monolith} color={GOLD} opacity={0.85} position={[0, 0, 0]} rotation={[0, 0.5, 0]} />
      <Edge geo={g.ground} color={SILVER} opacity={0.3} position={[0, -2.5, 0]} />
    </>
  );
}
function FormationRing({ g }) {
  return (
    <>
      <Loop geo={g.ringA} color={GOLD} opacity={0.85} rotation={[0.25, 0.35, 0]} />
      <Loop geo={g.ringB} color={SILVER} opacity={0.35} rotation={[0.25, 0.35, 0]} />
    </>
  );
}
function FormationPortal({ g }) {
  return (
    <>
      <Edge geo={g.portalA} color={GOLD} opacity={0.75} position={[0, 0, 0]} />
      <Edge geo={g.portalB} color={SILVER} opacity={0.3} position={[0, 0, -1.6]} />
    </>
  );
}

const FORMATIONS = [null, FormationWeb, FormationCloth, FormationMonolith, FormationRing, FormationPortal];

// ---------------------------------------------------------------------------
// Origami-Welt: faltet zwischen Formationen, kamera-gescrubbt
// ---------------------------------------------------------------------------
function OrigamiWorld({ progress, keyframes, kfTick, fx }) {
  const groupRefs = useRef([]);
  const stateRef = useRef({ prevW: 0, vel: 0 });
  const invalidate = useThree((s) => s.invalidate);

  const g = useMemo(
    () => ({
      panelA: rectEdges(3.6, 2.3),
      lineA: segmentLine(-1.5, 0.55, 0, 0.6, 0.55, 0),
      panelB: rectEdges(4.4, 2.7),
      lineB: segmentLine(-1.9, 0.7, 0, 1.0, 0.7, 0),
      panelC: rectEdges(2.9, 1.9),
      cloth: clothGrid(12, 6, 12, 6),
      monolith: boxEdges(1.5, 4.6, 1.5),
      ground: circleLine(3.2, 96),
      ringA: circleLine(3.4),
      ringB: circleLine(2.6),
      portalA: rectEdges(5.2, 3.3),
      portalB: rectEdges(6.4, 4.1),
    }),
    []
  );
  useEffect(() => () => Object.values(g).forEach((geo) => geo.dispose()), [g]);

  // Nach Keyframe-Neumessung einmal neu zeichnen (auch ohne Scroll)
  useEffect(() => {
    invalidate();
  }, [kfTick, invalidate]);

  useFrame(({ camera }, delta) => {
    const W = progress.get();
    const kf = keyframes.current;
    const st = stateRef.current;

    // Scroll-Geschwindigkeit (leichter Glow-Boost), geglättet
    const dt = Math.min(Math.max(delta, 1 / 240), 0.1);
    st.vel = lerp(st.vel, Math.min(Math.abs(W - st.prevW) / dt, 1.4), 0.12);
    st.prevW = W;

    // Aktives Keyframe-Segment
    let segIdx = 0;
    for (let i = 0; i <= kf.length - 2; i++) {
      if (W >= kf[i][0]) segIdx = i;
      else break;
    }
    const [p0, f0] = kf[segIdx];
    const [p1, f1] = kf[segIdx + 1];
    const t = smooth(seg(W, p0, p1));
    const morphing = f0 !== f1;

    for (let f = 1; f < FORMATIONS.length; f++) {
      const grp = groupRefs.current[f];
      if (!grp) continue;
      // Sichtbarkeitsanteil: 1 = voll entfaltet, 0 = weg
      let fold = 0; // 0..1 → wie weit ZUSAMMENGEFALTET
      let rise = 1; // 0..1 → wie weit HOCHGEZOGEN
      let show = false;
      if (f === f0 && f === f1) {
        show = true;
      } else if (morphing && f === f0) {
        show = t < 0.5;
        fold = smooth(seg(t, 0, 0.5));
      } else if (morphing && f === f1) {
        show = t > 0.5;
        rise = smooth(seg(t, 0.5, 1));
      }
      grp.visible = show;
      if (!show) continue;
      // Zusammenfalten: nach hinten kippen + auf eine Linie sinken
      grp.rotation.x = fold * Math.PI * 0.5;
      grp.scale.y = Math.max(0.001, (1 - fold) * rise);
      // Hochziehen: von unten in Position gleiten
      grp.position.y = (1 - rise) * -2.4;
      // Leichte Drift im Halt (kein Nachlaufen — rein aus W abgeleitet)
      grp.rotation.y = Math.sin(W * Math.PI * 4 + f) * 0.08;
    }

    fx.current.vel = st.vel;
    fx.current.morph = morphing ? Math.sin(t * Math.PI) : 0;

    // Kamera: sanfter Drift über die gesamte Reise
    camera.position.x = Math.sin(W * Math.PI * 2) * 0.6;
    camera.position.y = 0.45 - W * 0.9;
    camera.lookAt(0, 0.1 - W * 0.4, 0);
  });

  return (
    <>
      {FORMATIONS.map((F, i) =>
        F ? (
          <group key={i} ref={(el) => (groupRefs.current[i] = el)} visible={false}>
            <F g={g} />
          </group>
        ) : null
      )}
    </>
  );
}

// Cineastischer Film-Look (nur Desktop): weiches Glühen der Linien + Vignette.
function CinematicPost({ fx }) {
  const bloomRef = useRef(null);
  useFrame(() => {
    const { vel, morph } = fx.current;
    if (bloomRef.current) bloomRef.current.intensity = 0.7 + vel * 0.8 + morph * 0.5;
  });
  return (
    <EffectComposer multisampling={0}>
      <Bloom ref={bloomRef} mipmapBlur intensity={0.7} luminanceThreshold={0.12} luminanceSmoothing={0.4} radius={0.75} />
      <Vignette offset={0.32} darkness={0.6} />
    </EffectComposer>
  );
}

// Sleep/Wake-Polling: rendert nur bei echter Scroll-Änderung.
function ScrollInvalidator({ progress }) {
  const invalidate = useThree((s) => s.invalidate);
  useEffect(() => {
    let raf = null;
    let last = -1;
    let idleFrames = 0;
    let sleeping = false;
    const poll = () => {
      const v = progress.get();
      if (v !== last) {
        last = v;
        idleFrames = 0;
        invalidate();
      } else {
        idleFrames += 1;
        if (idleFrames >= 60) {
          sleeping = true;
          raf = null;
          return;
        }
      }
      raf = requestAnimationFrame(poll);
    };
    const wake = () => {
      if (sleeping) {
        sleeping = false;
        idleFrames = 0;
        if (raf === null) raf = requestAnimationFrame(poll);
      }
    };
    raf = requestAnimationFrame(poll);
    window.addEventListener("scroll", wake, { passive: true });
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", wake);
    };
  }, [progress, invalidate]);
  useEffect(() => {
    const iv = setInterval(invalidate, 400);
    const to = setTimeout(() => clearInterval(iv), 2500);
    return () => {
      clearInterval(iv);
      clearTimeout(to);
    };
  }, [invalidate]);
  return null;
}

export default function WorldCanvas({ progress, worldRef, layoutKey }) {
  const reduced = useReducedMotion();
  const isMobile =
    typeof window !== "undefined" &&
    (window.innerWidth < 768 || !window.matchMedia("(pointer: fine)").matches);

  const keyframesRef = useRef(DEFAULT_KF);
  const [kfTick, setKfTick] = useState(0); // steigt nach jeder Messung → Redraw
  const fxRef = useRef({ vel: 0, morph: 0 });

  const measure = useCallback(() => {
    const worldEl = worldRef?.current;
    if (!worldEl) return;
    const kf = measureKeyframes(worldEl);
    keyframesRef.current = kf && kf.length >= 4 ? kf : DEFAULT_KF;
    setKfTick((t) => t + 1);
  }, [worldRef]);

  // Messungen: Mount, Fonts, Bilder (verzögert), Resize (debounced)
  useEffect(() => {
    measure();
    const timeouts = [800, 1500, 3000].map((ms) => setTimeout(measure, ms));
    if (document.fonts?.ready) document.fonts.ready.then(measure).catch(() => {});
    let resizeTimer = null;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, 150);
    };
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      timeouts.forEach(clearTimeout);
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
    };
  }, [measure]);

  // Sections ein-/ausgeblendet (KI-Manager) → Layout neu vermessen
  useEffect(() => {
    const t1 = setTimeout(measure, 80);
    const t2 = setTimeout(measure, 400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [layoutKey, measure]);

  // Reduced Motion: keine Welt-Animation — die dunklen Sections bleiben statisch
  if (reduced) return null;

  return (
    <div
      className="fixed inset-0 z-0 pointer-events-none"
      data-testid="world-canvas"
      aria-hidden="true"
    >
      <Canvas
        frameloop="demand"
        dpr={isMobile ? [1, 1.25] : [1, 1.6]}
        gl={{ antialias: !isMobile, alpha: false, powerPreference: "high-performance", preserveDrawingBuffer: true }}
        camera={{ fov: 50, position: [0, 0.45, 8], near: 0.1, far: 40 }}
        style={{ position: "absolute", inset: 0 }}
      >
        <color attach="background" args={["#050505"]} />
        <fogExp2 attach="fog" args={["#050505", 0.05]} />
        <ScrollInvalidator progress={progress} />
        <OrigamiWorld progress={progress} keyframes={keyframesRef} kfTick={kfTick} fx={fxRef} />
        {!isMobile && <CinematicPost fx={fxRef} />}
      </Canvas>
    </div>
  );
}
